import { ListActivityAPIQueryParams } from '@goldcast/api/backend';
import { assetsClipTranslateClipCreate, ClipAssetResponse } from '@goldcast/api/content';
import { current } from 'immer';
import { pick } from 'radash';
import { CaptionPresetName } from '@goldcast/player-config/src/caption-presets/types';
import { FeatureFlags, FeatureFlagTypeMap } from '@goldcast/player-config/src/classes/FeatureFlags';
import { MAIN_TRACK_ID, SPEAKER_LABEL_DEFAULT_TRANSITIONS } from '@goldcast/player-config/src/constants/player';
import { fetchAlignedRecordings } from '@goldcast/player-config/src/retrievers/fetchAlignedRecordings';
import { loadClip } from '@goldcast/player-config/src/retrievers/loadClip';
import { Scenes } from '@goldcast/player-config/src/types/RemotionPlayerConfigTypes/Scene';
import { SegmentType } from '@goldcast/player-config/src/types/Segment/Type';
import {
  type CaptionsPosition,
  type CaptionStyles,
  type HighlightType,
  type LayoutType,
  type SizeType
} from '@goldcast/player-config/src/types/asset-types';
import {
  type Clip,
  AlignedRecordings,
  ClipMetadata,
  DeletedRange,
  LanguageOption
} from '@goldcast/player-config/src/types/clip-types';
import { type SpeakerWithDetails } from '@goldcast/player-config/src/types/speaker-types';
import { getAlignedRecordings } from '@goldcast/player-config/src/utils/alignedRecordings';
import {
  normalizeClipMetadataConfigForDeleteFlag,
  updateIntroOutroDurationsIfNeeded
} from '@goldcast/player-config/src/utils/assetMetadataUtils';
import {
  getAllCompositeSegments,
  getAllSegments,
  getClipBroadcastId,
  isClipFromUploads,
  isClipPreRecordedEvent,
  isLayoutWithVisionDataEnabledPure,
  isSplitsInTimelineEnabledPure,
  normalizeClipConfigForDeleteFlag
} from '@goldcast/player-config/src/utils/clipUtils';
import { getSegmentsTrack } from '@goldcast/player-config/src/utils/playerConfigGetters';
import { migrateSpeakerLabelStyles } from '@goldcast/player-config/src/utils/speakerLabelUtils';
import { ContentLabInitClip } from './ContentLabInitClip';
import { updateHistory } from './clipHistory';
import featureFlagStore from './featureFlagStore';
import { updateClipConfig } from './sharedAPI/sharedAPI';
import createStore from './store';
import { getPlayerConfigHelper } from '@/App/remotion/MainPlayer/utils/playerConfig/config';
import { computeCaptionsForClip } from '@/App/remotion/libs/caption/caption';
import { LANGUAGE_OPTIONS } from '@/Pages/Clip/CaptionTranslation/types';
import { updateClipData } from '@/Pages/Clip/ClipPlayer/ClipNewTimeline/clipUpdateDataUtils';
import { CLIP_UPDATE_TYPE } from '@/Pages/Clip/ClipPlayer/ClipNewTimeline/clipUpdateDataUtils/constants';
import { propertiesToCompareV1 } from '@/Pages/Clip/SideBar/Templates/utils';
import { PreviewTemplate } from '@/Pages/Clip/SideBar/types';
import { getLayoutAvailabilityStatus } from '@/context/ClipsContext/ClipsContextUtils';
import {
  getAnalyticsClipEditType,
  getClipAnalyticsPayload,
  getClipAnalyticsPayloadV2
} from '@/hooks/utils/analyticsHookUtil';
import { isDuplicateRecordingAsset } from '@/libs/duplicateAsset';
import { CustomEvents } from '@/libs/eventBus/constants';
import EventBus from '@/libs/eventBus/eventBus';
import { getVideoDurationFromSrc } from '@/libs/getVideoDuration';
import { showErrorToast, showSuccessToast } from '@/libs/toast/toast';
import { debounce, isEmptyObject } from '@/libs/utils';
import { FeatureFlagKeys } from '@/services/featureFlag';
import { useContentMetadataStore } from '@/stores/contentMetaDataStore/contentMetaDataStore';

type CurrentClips = Record<string, Clip>;

type UpdateIntroOutroProps = {
  url?: string;
  duration?: number;
};

export const allClips = createStore<Clip[]>([]);
export const canGenerateClips = createStore<boolean>(true);

/**
 * @deprecated This store will no longer be meaningful once we move Fast Edit to 100%
 */
export const clipCaptionsLoader = createStore<Record<string, boolean>>({});

export const templateLoader = createStore<boolean>(false);
export const clipGeneratingCount = createStore<number>(0);
// loader for clip translation
export const clipTranslationLoader = createStore<Record<string, boolean>>({});

/**
 * Used to store boolean flags against clip ids of a freshly loaded clip which don't have download configs.
 * The download configs are generated and applied after a clip is loaded along with its other dependencies like speaker analysis
 */
export const clipsNeedingConfigUpdateStore = createStore<{
  [clipId: string]: boolean;
}>({});

/**
 * @deprecated This function will no longer be meaningful once we move Fast Edit to 100%
 */
export function updateClipCaptionsLoadingState({
  clipId,
  loading,
  forceRespectPayload = false
}: {
  clipId: string;
  loading: boolean;
  forceRespectPayload?: boolean;
}) {
  let finalLoadingState = false;

  if (forceRespectPayload) {
    finalLoadingState = loading;
  }

  clipCaptionsLoader.update(data => ({
    ...data,
    [clipId]: finalLoadingState
  }));
}

export function updateClipTranslationLoadingState(clipId: string, loading: boolean) {
  clipTranslationLoader.update(data => ({
    ...data,
    [clipId]: loading
  }));
}

/**
 * Used to render the integrated player and editor for a clip
 */
export const currentClip = createStore<CurrentClips>({});

/**
 * Returns the persisted speaker_mapping for the content backing the given clip.
 *
 * Resolves the canonical content/broadcast id from the clip (via `getClipBroadcastId`)
 * and reads from `useContentMetadataStore`, which is keyed by content id. Falls back to
 * an empty mapping when no clip id is provided or no broadcast id can be derived.
 * The clip id is NOT used as a substitute key into `useContentMetadataStore`.
 */
export function getSpeakerMappingForClip(clipId?: string): Record<string, number[]> {
  if (!clipId) {
    return {};
  }

  const clip = currentClip.getSnapshot()[clipId];
  const broadcastId = clip ? getClipBroadcastId(clip) : null;
  if (!broadcastId) {
    return {};
  }
  const contentMetadata = useContentMetadataStore.getState().data[broadcastId];
  return contentMetadata?.metadata?.speakerAnalysis?.speaker_mapping ?? {};
}

export const logMigrationEvent = (clip: Clip, name: string) => {
  window.analytics?.track('ContentLabEvent', {
    name,
    isSuccess: true,
    ...getClipAnalyticsPayloadV2(clip)
  });
};

export async function initClip(
  id: string,
  {
    shared = false,
    isSavedSearchPage = false,
    isSmartEventsPage = false
  }: { shared?: boolean; isSavedSearchPage: boolean; isSmartEventsPage: boolean }
): Promise<Clip> {
  const featureFlagInstance = new FeatureFlags(featureFlagStore.getSnapshot() as FeatureFlagTypeMap);
  const clipInitializer = new ContentLabInitClip({
    clipId: id,
    isSharedPage: shared,
    isSavedSearchPage,
    isSmartEventsPage,
    contentMetadataStoreInstance: useContentMetadataStore
  });
  clipInitializer.setFlagInstance(featureFlagInstance);
  const { clip } = await clipInitializer.initializeClip();

  // A duplicate recording must always be opened via the recordings route, which carries an
  // `activeDuplicateId` param. If the API response says this clip is a duplicate but the route
  // has no `activeDuplicateId`, it was opened through the wrong entry point — a state that should
  // never happen, regardless of which other params are present.
  const hasActiveDuplicateId = !!new URLSearchParams(window.location.search).get('activeDuplicateId');
  if (isDuplicateRecordingAsset(clip) && !hasActiveDuplicateId) {
    window.logger?.error(
      'Duplicate recording opened without activeDuplicateId — this should not happen',
      {
        clipId: id,
        route: `${window.location.pathname}${window.location.search}`
      }
    );
  }

  return clip;
}

function mapVideoIdsToAlignedRecordings(videoIds: string[], alignedRecordingsStore: AlignedRecordings[]): string[] {
  return videoIds
    .map(id => {
      const recording = alignedRecordingsStore.find(r => r.associatedCloudRecordingId === id);

      return recording?.id;
    })
    .filter(a => a !== undefined);
}

interface SaveClipChangesProps {
  clipId: string;
  shouldGenerateThumbnail?: boolean;
  shouldAddToHistory?: boolean;
  isEditedState?: boolean;
  isTranslatedCaptionEdited?: boolean;
  skipRemotionUpdate?: boolean;
  onSaveSuccess?: (clip: Clip) => void;
  onFailure?: ({ error, clip }: { error: Error; clip: Clip }) => void;
}

/**
 * @deprecated This function is deprecated and will be removed in future releases. Use updateClipData instead.
 */
export const saveClipChanges: (props: SaveClipChangesProps) => void = debounce(
  ({
    clipId,
    shouldAddToHistory = true,
    isEditedState = true,
    isTranslatedCaptionEdited = false,
    skipRemotionUpdate,
    onSaveSuccess,
    onFailure
  }: SaveClipChangesProps) => {
    const clip = Object.assign({}, currentClip.getSnapshot()[clipId]);
    if (isEmptyObject(clip)) {
      return;
    }

    if (shouldAddToHistory) {
      updateHistory(clipId, clip.asset_metadata);
    }

    clip.asset_metadata = {
      ...clip.asset_metadata,
      is_edited: isEditedState,
      isLayoutProcessing:
        getLayoutAvailabilityStatus(clip.asset_metadata.layout, clip.layout_status, clip) === 'PROCESSING'
    };

    const clipUpdatePromise = updateClipConfig({ clipId, clip, isTranslatedCaptionEdited, skipRemotionUpdate });

    clipUpdatePromise.then(value => {
      templateLoader.set(() => false);
      updateClipWithId(clipId, { updated_at: value.updated_at }, false, true);
      if (onSaveSuccess) {
        onSaveSuccess(currentClip.getSnapshot()[clipId]);
      }
    });

    clipUpdatePromise.catch(error => {
      window.logger?.error('Error updating clip', error);
      window.analytics?.track('ContentLabEvent', {
        name: 'ContentEdited',
        isSuccess: false,
        ...getClipAnalyticsPayload(clip)
      });
      if (onFailure) {
        onFailure({ clip, error });
      }
    });
  }
);

export function deleteWords(clipId: string, toDelete: DeletedRange) {
  updateClipData({
    clipId,
    actionType: CLIP_UPDATE_TYPE.ADD_COMPOSITE_SEGMENT_DELETE_WORDS,
    startTime: toDelete.bounds[0],
    endTime: toDelete.bounds[1],
    deleteType: toDelete.deleteType || 'MANUAL'
  });
}

export function toggleCaptions(clipId: string, enabled: boolean) {
  updateClipData({
    clipId,
    actionType: CLIP_UPDATE_TYPE.TOGGLE_CAPTIONS,
    enabled
  });
}

export function toggleHideBorders(clipId: string, showBorders: boolean) {
  updateClipData({
    clipId,
    actionType: CLIP_UPDATE_TYPE.UPDATE_CLIP_BORDERS_VISIBILITY,
    hideBorders: showBorders
  });
}

export function toggleSpeakerLabels(clipId: string, enabled: boolean, shouldUpdateConfig: boolean = true) {
  updateClipData(
    {
      clipId,
      actionType: CLIP_UPDATE_TYPE.UPDATE_CLIP_SPEAKER_LABELS,
      showSpeakerLabels: enabled
    },
    {
      skipBackendUpdate: !shouldUpdateConfig
    }
  );

  if (!enabled) {
    updateSpeakerLabelStyles(clipId, {
      transitions: SPEAKER_LABEL_DEFAULT_TRANSITIONS
    });
  }
}

export function selectMagicLayout(clipId: string, magicLayoutType: LayoutType) {
  updateClipData({
    clipId,
    actionType: CLIP_UPDATE_TYPE.UPDATE_CLIP_LAYOUT,
    layout: magicLayoutType
  });
}

export function changeSize(clipId: string, size: SizeType) {
  updateClipData({
    clipId,
    actionType: CLIP_UPDATE_TYPE.UPDATE_CLIP_SIZE,
    size
  });
}

export function changeCaptionsPosition({
  clipId,
  caption_positions,
  skipUpdate = false
}: {
  clipId: string;
  caption_positions: CaptionsPosition;
  skipUpdate?: boolean;
}) {
  updateClipData(
    {
      clipId,
      actionType: CLIP_UPDATE_TYPE.UPDATE_CAPTIONS_POSITION,
      captionPositions: caption_positions
    },
    {
      skipBackendUpdate: skipUpdate
    }
  );
}

export function updateCaptionStyles(
  clipId: string,
  captionStyles: Partial<CaptionStyles>,
  shouldAddToHistory: boolean = true
) {
  updateClipData(
    {
      clipId,
      actionType: CLIP_UPDATE_TYPE.UPDATE_CAPTION_STYLES,
      captionStyles
    },
    {
      skipBackendUpdate: !shouldAddToHistory
    }
  );
}

export function updateCaptionPreset(clipId: string, presetName: CaptionPresetName) {
  updateClipData({
    clipId,
    actionType: CLIP_UPDATE_TYPE.UPDATE_CAPTION_PRESET,
    presetName
  });
}

export function updateIntroOutro(clipId: string, key: 'intro' | 'outro', { url, duration }: UpdateIntroOutroProps) {
  updateClipData({
    clipId,
    actionType: CLIP_UPDATE_TYPE.ADD_SEGMENT_TRACK,
    type: SegmentType.VIDEO,
    id: key,
    url: url as string,
    duration: duration as number
  });
}

export function updateStartEndTime(clipId: string, key: 'start' | 'end', value?: number, duration?: number) {
  updateClipData({
    clipId,
    actionType: CLIP_UPDATE_TYPE.UPDATE_SEGMENT_TRACK,
    type: SegmentType.COMPOSITE,
    compositeId: 'compositeId',
    trackId: MAIN_TRACK_ID,
    startTime: key === 'start' ? value : undefined,
    endTime: key === 'end' ? value : undefined,
    duration: key === 'end' && duration ? duration : undefined
  });
}

export function updateCaptionFontLocation(clipId: string, path: string) {
  updateClipData({
    clipId,
    actionType: CLIP_UPDATE_TYPE.UPDATE_CLIP_FONT_LOCATION,
    path
  });
}

export function updateSpeakerLabelStyles(
  clipId: string,
  partialSpeakerLabelStyles: Partial<Clip['asset_metadata']['speaker_label_styles']>
) {
  updateClipData({
    clipId,
    actionType: CLIP_UPDATE_TYPE.UPDATE_CLIP_SPEAKER_LABEL_STYLES,
    speakerLabelsStyles: partialSpeakerLabelStyles
  });
}

export function updateHighlightType(clipId: string, highlightType: HighlightType) {
  updateClipData({
    clipId,
    actionType: CLIP_UPDATE_TYPE.UPDATE_WORD_HIGHLIGHT,
    updatePayload: {
      highlightType
    }
  });
}

export function updateHighlightColor(clipId: string, highlightColor?: string) {
  updateClipData({
    clipId,
    actionType: CLIP_UPDATE_TYPE.UPDATE_WORD_HIGHLIGHT,
    updatePayload: {
      highlightColor
    }
  });
}

export function updateTextHighlightColor(clipId: string, highlightColor?: string) {
  updateClipData({
    clipId,
    actionType: CLIP_UPDATE_TYPE.UPDATE_WORD_HIGHLIGHT,
    updatePayload: {
      textHighlightColor: highlightColor
    }
  });
}

export function applyTemplate(clipId: string, template: PreviewTemplate) {
  const clip = currentClip.getSnapshot()[clipId];

  let updatedScenes: Scenes = [];

  if (clip.asset_metadata.scenes && clip.asset_metadata.scenes.length > 0) {
    updatedScenes = clip.asset_metadata.scenes;

    if (template.layout === 'AUDIOGRAM' && updatedScenes.length > 0) {
      const firstScene = updatedScenes[0];
      const lastScene = updatedScenes[updatedScenes.length - 1];

      updatedScenes = [
        {
          ...firstScene,
          toFrame: lastScene.toFrame,
          details: {
            ...firstScene.details,
            fitMode: template.fit,
            layout: template.layout as LayoutType
          }
        }
      ];
    } else {
      updatedScenes = clip.asset_metadata.scenes.map(scene => ({
        ...scene,
        details: {
          ...scene.details,
          fitMode: template.fit,
          layout: template.layout as LayoutType
        }
      }));
    }
  }

  const hasCompleteIntroOutroInformation =
    (template.intro ? !!template.introDuration : true) && (template.outro ? !!template.outroDuration : true);
  const useAsyncClipUpdate = !hasCompleteIntroOutroInformation;
  const clipUpdateFunction = useAsyncClipUpdate ? updateClipMetadataWithIdAsync : updateClipMetadataWithId;

  clipUpdateFunction(clipId, {
    ...pick(template, propertiesToCompareV1 as (keyof PreviewTemplate)[]),
    ...(updatedScenes.length > 0 ? { scenes: updatedScenes } : {}),
    is_edited: false,
    template_id: template.id,
    speaker_label_styles: migrateSpeakerLabelStyles(template.speaker_label_styles)
  });

  const isSplitsEnabled = isSplitsInTimelineEnabledPure({
    clip: clip,
    isSplitsTranslationEnabled: !!featureFlagStore.getSnapshot()[FeatureFlagKeys.Use_CL_Splits_Translations]
  });
  if (isSplitsEnabled) {
    applySplitChangesFromTemplate(clip, template);
  }

  const captionsPositions = template.caption_positions;
  if (captionsPositions) {
    const { x, y, width, height } = captionsPositions;

    EventBus.dispatch(CustomEvents.UpdateCaptionsPosition, {
      left: parseFloat(x as unknown as string) + '%',
      top: parseFloat(y as unknown as string) + '%',
      width: parseFloat(width as unknown as string) + '%',
      height: parseFloat(height as unknown as string) + '%'
    });
  }

  templateLoader.set(() => true);
  saveClipChanges({ clipId, shouldGenerateThumbnail: true, shouldAddToHistory: true, isEditedState: false });
  trackClipUpdate(clipId, 'LAYOUT_CHANGE');
}

function applySplitChangesFromTemplate(clip: Clip, template: PreviewTemplate) {
  if (clip.asset_metadata.player_config) {
    const track = getSegmentsTrack(clip.asset_metadata.player_config);
    const introTrack = track?.segments.find(segment => segment.id === 'intro');
    const outroTrack = track?.segments.find(segment => segment.id === 'outro');
    if (introTrack) {
      updateClipData({
        clipId: clip.id,
        actionType: CLIP_UPDATE_TYPE.REMOVE_SEGMENT_TRACK,
        id: introTrack.id
      });
    }
    if (outroTrack) {
      updateClipData({
        clipId: clip.id,
        actionType: CLIP_UPDATE_TYPE.REMOVE_SEGMENT_TRACK,
        id: outroTrack.id
      });
    }
    updateClipData({
      clipId: clip.id,
      actionType: CLIP_UPDATE_TYPE.UPDATE_CLIP_LAYOUT,
      layout: template.layout
    });
  }

  if (template.intro) {
    updateClipData({
      clipId: clip.id,
      actionType: CLIP_UPDATE_TYPE.ADD_SEGMENT_TRACK,
      type: SegmentType.VIDEO,
      id: 'intro',
      url: template.intro as string,
      duration: template.introDuration as number
    });
  }
  if (template.outro) {
    updateClipData({
      clipId: clip.id,
      actionType: CLIP_UPDATE_TYPE.ADD_SEGMENT_TRACK,
      type: SegmentType.VIDEO,
      id: 'outro',
      url: template.outro as string,
      duration: template.outroDuration as number
    });
  }
}

export function getActiveCaptionsBackgroundColor(
  clipMetadata: ClipMetadata | PreviewTemplate,
  highlightType?: HighlightType,
  backgroundColor?: string
): string | undefined {
  const highlight = highlightType || clipMetadata.highlight_type;
  const bgColor = backgroundColor || clipMetadata.word_highlight_color;
  return highlight === 'box' ? bgColor : 'transparent';
}

export function getInactiveCaptionsOpacityClass(
  clipMetadata: ClipMetadata | PreviewTemplate,
  highlightType?: HighlightType
): number {
  const highlight = highlightType || clipMetadata.highlight_type;
  switch (highlight) {
    case 'text':
      return clipMetadata.caption_styles?.animation === 'Background' ? 1 : 0.5;
    default:
      return 1;
  }
}

// !! Hitesh Need to check where is the use case
export function updateVisibleFaceIdsInClip(clipId: string, visible_face_ids: number[]) {
  updateClipData({
    clipId,
    actionType: CLIP_UPDATE_TYPE.UPDATE_VISIBLE_FACE_IDS,
    visibleFaceIds: visible_face_ids
  });
}

export function updateVisibleSpeakers(clipId: string, visible_speakers: SpeakerWithDetails[]) {
  updateClipData({
    clipId,
    actionType: CLIP_UPDATE_TYPE.UPDATE_VISIBLE_SPEAKERS,
    visibleSpeakers: visible_speakers
  });
}

export function updateVisibleVideoIds(clipId: string, visible_video_ids: string[]) {
  updateClipData({
    clipId,
    actionType: CLIP_UPDATE_TYPE.UPDATE_VISIBLE_VIDEO_IDS,
    visibleVideoIds: visible_video_ids
  });
}

/**
 * @deprecated This function is deprecated and should not be used in new implementations.
 */
export function updateClipWithId(
  clipId: string,
  newClipData: Partial<Clip>,
  resetData?: boolean,
  skipRemotionUpdate?: boolean,
  isSharedPage?: boolean
) {
  currentClip.update(data => {
    const clip = data[clipId] ? current(data[clipId]) : undefined;

    const newClip = resetData
      ? newClipData
      : {
          ...clip,
          ...newClipData
        };

    const normalizedClip = normalizeClipConfigForDeleteFlag(newClip as Clip);

    return getFinalClipWithRemotionConfig({
      allClips: data,
      clip: normalizedClip as Clip,
      skipRemotionUpdate,
      shouldUpdatePlayerConfig: true,
      isSharedPage
    });
  });
}

export function updateClipMetadataWithId(
  clipId: string,
  newMetadata: Partial<Clip['asset_metadata']>,
  shouldUpdatePlayerConfig?: boolean
) {
  currentClip.update(data => {
    const clip = current(data[clipId]);
    const normalizedMetadata = normalizeClipMetadataConfigForDeleteFlag(newMetadata);

    const newClip = {
      ...clip,
      asset_metadata: {
        ...clip.asset_metadata,
        ...normalizedMetadata
      }
    };

    return getFinalClipWithRemotionConfig({
      allClips: data,
      clip: newClip as Clip,
      skipRemotionUpdate: false,
      shouldUpdatePlayerConfig: shouldUpdatePlayerConfig ?? true
    });
  });
}

/**
 * ! Use this wisely
 * This is only a hack to get things to work when applying a template
 */

export async function updateClipMetadataWithIdAsync(
  clipId: string,
  newMetadata: Partial<Clip['asset_metadata']>,
  shouldUpdatePlayerConfig?: boolean
) {
  // Update intro/outro durations if needed
  const { updatedMetadata } = await updateIntroOutroDurationsIfNeeded({
    fetchVideoDuration: getVideoDurationFromSrc,
    metadata: newMetadata
  });

  updateClipMetadataWithId(clipId, updatedMetadata, shouldUpdatePlayerConfig);
}

export function overwriteAllClips(newClips: Clip[]) {
  const currentClipData = currentClip.getSnapshot();

  allClips.set(() => {
    return newClips.map(clip => {
      const currentClip = currentClipData[clip.id];
      // Data that should not be overwritten on clip list reload
      if (currentClip?.asset_metadata) {
        return {
          ...clip,
          asset_metadata: {
            ...clip.asset_metadata,
            captions: currentClip.asset_metadata.captions
          }
        };
      }

      return clip;
    });
  });
}

export function appendAllClips(clips: Clip[]) {
  allClips.update(data => [...data, ...clips]);
}

export function prependAllClips(clips: Clip[]) {
  allClips.update(data => [...clips, ...data]);
}

export function clearAllClips() {
  allClips.set(() => []);
}

export function updateClipInList(updatedClip: Clip) {
  const normalizedClip = normalizeClipConfigForDeleteFlag(updatedClip);
  allClips.update(allClipsData => allClipsData.map(clip => (clip.id === normalizedClip.id ? normalizedClip : clip)));
}

export function onDuplicateClipSuccess(clip: ClipAssetResponse): Promise<void> {
  return new Promise(resolve => {
    prependAllClips([{ ...clip, isRemoved: false } as unknown as Clip]);

    setTimeout(() => {
      resolve();
    }, 200);
  });
}

export function removeClip(clipId: string): Promise<void> {
  return new Promise(resolve => {
    // Set isRemoved property to true, to animate removing, after short timeout - remove from list
    allClips.update(data =>
      data.map(clip => {
        return {
          ...clip,
          // 1st occurrence of condition check
          isRemoved: clipId === clip.asset_metadata.id || clipId === clip.id ? true : clip.isRemoved
        };
      })
    );

    setTimeout(() => {
      // 2nd occurrence of condition check
      allClips.update(data => data.filter(clip => clipId !== clip.asset_metadata.id && clipId !== clip.id));
      resolve();
    }, 500);
  });
}

export function setCanGenerateClips(canGenerate: boolean) {
  canGenerateClips.set(() => canGenerate);
}

export function trackClipUpdate(clipId: string, entityUpdated: string, payload?: Record<string, any>) {
  const edit_type = payload?.edit_type || getAnalyticsClipEditType(entityUpdated);

  window.analytics?.track('ContentLabEvent', {
    name: 'ContentEdited',
    entityUpdated,
    editType: edit_type,
    ...payload,
    ...getClipAnalyticsPayload(currentClip.getSnapshot()[clipId])
  });
}

export function setClipGeneratingCount(count: number) {
  clipGeneratingCount.set(() => count);
}

/**
 * @deprecated This function is deprecated and should not be used in new implementations.
 */
export function getFinalClipWithRemotionConfig({
  allClips,
  clip,
  skipRemotionUpdate,
  shouldUpdatePlayerConfig,
  isSharedPage
}: {
  allClips: CurrentClips;
  clip: Clip;
  skipRemotionUpdate?: boolean;
  shouldUpdatePlayerConfig?: boolean;
  isSharedPage?: boolean;
}) {
  const clipId = clip.id;

  if (!skipRemotionUpdate && !isSharedPage) {
    try {
      const remotionConfig = undefined;
      const player_config = getPlayerConfigHelper({ clip, shouldUpdatePlayerConfig });

      return {
        ...allClips,
        [clipId]: {
          ...clip,
          asset_metadata: {
            ...clip.asset_metadata,
            remotionConfig,
            player_config
          }
        }
      };
    } catch (e) {
      window.logger?.error('Error while updating elements timeline', e);
    }
  }

  return {
    ...allClips,
    [clipId]: clip
  };
}

export const shouldUseRemotionConfig = (clip: Clip) => {
  const showCaptions = !!clip.asset_metadata?.subtitle;
  const showSpeakerLabels = !!clip.asset_metadata?.magicLayout?.showSpeakerLabels;

  return showCaptions || showSpeakerLabels;
};

export const isScreenShareDetectionFeatureFlagEnabled = () => {
  const featureFlags = featureFlagStore.getSnapshot();
  return !!featureFlags[FeatureFlagKeys.Use_CL_Screenshare_Detection];
};

export const isPlayRangeMicroCutsEnabled = () => {
  const featureFlags = featureFlagStore.getSnapshot();
  return !!featureFlags[FeatureFlagKeys.Use_CL_Play_Range_Micro_Cuts];
};

export const isFullVideoFaceRecEnabled = () => {
  const featureFlags = featureFlagStore.getSnapshot();
  return !!featureFlags[FeatureFlagKeys.Use_CL_Fullvideo_Facerec];
};

export const isFullVideoFaceRecForPreRecordedEnabled = () => {
  const featureFlags = featureFlagStore.getSnapshot();
  return !!featureFlags[FeatureFlagKeys.Use_CL_Fullvideo_Facerec_Pre_Recorded];
};

export const isSimplifiedUploadEnabled = () => {
  const featureFlags = featureFlagStore.getSnapshot();
  return !!featureFlags[FeatureFlagKeys.Use_CL_Simplified_Upload];
};

/**
 * Picks the full-video face rec flag that governs a given clip: pre-recorded events are gated by
 * their own flag (rolled out independently of uploads), everything else uses the upload flag.
 */
export const isFullVideoFaceRecEnabledForClip = (clip: Clip) =>
  isClipPreRecordedEvent(clip) ? isFullVideoFaceRecForPreRecordedEnabled() : isFullVideoFaceRecEnabled();

/**
 * Confirms the full-video face rec pipeline actually ran for the clip by inspecting the
 * stored speakers analysis. The feature flag alone is not sufficient, because even with the
 * flag on the full-video analysis can be skipped or fall back to the per-clip backend.
 */
export const didFullVideoFaceRecRunForClip = (clip: Clip): boolean => {
  const broadcastId = getClipBroadcastId(clip);
  if (!broadcastId) {
    return false;
  }
  const speakerAnalysis = useContentMetadataStore.getState().data[broadcastId]?.metadata.speakerAnalysis;
  return speakerAnalysis?.face_rec_backend === 'arcface';
};

export const isLayoutsWithVisionDataEnabled = (clip: Clip) => {
  return isLayoutWithVisionDataEnabledPure({
    clip,
    isFullFaceRecEnabled: isFullVideoFaceRecEnabled(),
    isFullFaceRecForPreRecordedEnabled: isFullVideoFaceRecForPreRecordedEnabled()
  });
};

// This function determines if splits related actions can be performed or not.
// This handles the cases where first split is not yet created.
export const isSplitsFeatureEnabled = (featureFlags, clip?: Clip) => {
  if (!featureFlags[FeatureFlagKeys.Use_CL_Splits_In_Timeline]) {
    return false;
  }

  const allCompositeSegments = clip ? getAllCompositeSegments(clip) : [];
  const allVideoSegments = clip
    ? getAllSegments(clip).filter(
        segment => segment.type === SegmentType.VIDEO && segment.id !== 'intro' && segment.id !== 'outro'
      )
    : [];

  if (allCompositeSegments.length > 1) {
    return true;
  }

  if (allVideoSegments.length > 0) {
    return true;
  }

  if (clip && (isClipFromUploads(clip) || isClipPreRecordedEvent(clip))) {
    return didFullVideoFaceRecRunForClip(clip);
  }
  return true;
};

export function requestCaptionTranslation(clipId: string, language: LanguageOption) {
  updateClipTranslationLoadingState(clipId, true);

  assetsClipTranslateClipCreate({
    body: {
      target_language: language.code,
      asset_id: clipId
    }
  })
    .then(() => {
      showSuccessToast(`Translation to ${language.name} is in progress, please wait...`);
    })
    .catch(() => {
      updateClipTranslationLoadingState(clipId, false);
      showErrorToast('Translation failed. Please try again.');
    });
}

export async function onCaptionsTranslated(clipId: string) {
  updateClipTranslationLoadingState(clipId, false);
  const clip: Clip = await loadClip(clipId);

  if (!clip) {
    return;
  }

  const language = LANGUAGE_OPTIONS.find(l => l.code === clip?.asset_metadata?.selected_lang);
  if (language) {
    onLanguageChange(clipId, language);
  }

  window.analytics?.track('ContentLabEvent', {
    name: 'ContentEdited',
    entityUpdated: 'CAPTIONS_TRANSLATION',
    editType: getAnalyticsClipEditType('CAPTIONS_TRANSLATION'),
    ...getClipAnalyticsPayload(clip),
    selectedLanguage: clip?.asset_metadata?.selected_lang
  });
}

export async function onLanguageChange(
  clipId: string,
  language: LanguageOption,
  onLanguageChangeSuccess?: (clip: Clip) => void
) {
  try {
    updateClipCaptionsLoadingState({ clipId, loading: true, forceRespectPayload: true });
    const clip: Clip = await loadClip(clipId, `&selected_lang=${language.code}`);
    updateClipCaptionsLoadingState({ clipId, loading: false, forceRespectPayload: true });
    if (
      isSplitsInTimelineEnabledPure({
        clip: clip,
        isSplitsTranslationEnabled: !!featureFlagStore.getSnapshot()[FeatureFlagKeys.Use_CL_Splits_Translations]
      })
    ) {
      const player_config = currentClip.getSnapshot()[clipId].asset_metadata.player_config;
      updateClipMetadataWithId(clipId, { ...clip.asset_metadata, player_config }, true);
    } else {
      const { duration, excludes, captions } = computeCaptionsForClip(clip);
      updateClipMetadataWithId(clipId, { ...clip.asset_metadata, duration, excludes, captions }, false);
    }
  } catch (error) {
    window.logger?.error('Error updating clip', error);
    showErrorToast("Updating the clip didn't go as planned. Please try refreshing the page.");
    window.analytics?.track('ContentLabEvent', {
      name: 'ContentLanguageChange',
      isSuccess: false
    });
  }

  saveClipChanges({
    clipId,
    shouldGenerateThumbnail: false,
    shouldAddToHistory: true,
    onSaveSuccess: clip => {
      window.analytics?.track('ContentLabEvent', {
        name: 'ContentEdited',
        entityUpdated: 'LANGUAGE_CHANGE',
        editType: getAnalyticsClipEditType('LANGUAGE_CHANGE'),
        ...getClipAnalyticsPayload(clip),
        selectedLanguage: language.code
      });
      if (onLanguageChangeSuccess) {
        onLanguageChangeSuccess(clip);
      }
    }
  });
}

/**
 * @deprecated - This function is only used for the old clip composition status.
 * To be removed with removal of Use_CL_Only_Local_Or_Cloud flag.
 */
export async function updateClipAfterFetchingLatestAlignedRecordings(
  broadcastId: string,
  clipId: string,
  queryParams: ListActivityAPIQueryParams
) {
  await fetchAlignedRecordings({
    broadcast_id: broadcastId,
    queryParams,
    contentMetadataStoreInstance: useContentMetadataStore
  });

  const existingScenes = currentClip.getSnapshot()[clipId].asset_metadata.scenes;
  const recordings = getAlignedRecordings({ broadcastId, contentMetadataStoreInstance: useContentMetadataStore });

  if (existingScenes) {
    const newScenes = existingScenes.map(scene => ({
      ...scene,
      details: {
        ...scene.details,
        visible_video_ids: mapVideoIdsToAlignedRecordings(scene.details.visible_video_ids, recordings),
        all_video_ids: mapVideoIdsToAlignedRecordings(scene.details.all_video_ids, recordings)
      }
    }));

    updateClipMetadataWithId(clipId, {
      scenes: newScenes
    });
  }

  saveClipChanges({ clipId, shouldGenerateThumbnail: false, shouldAddToHistory: false });

  // TODO: @hardik-saini to remove this once we have a loader for clip updates
  return new Promise<void>(resolve => {
    setTimeout(() => {
      resolve();
    }, 5000);
  });
}

export const areSpeakerLayoutsEnabled = (clip: Clip) => {
  const broadcastId = getClipBroadcastId(clip) ?? '';
  const speakersAnalysis = useContentMetadataStore.getState().data[broadcastId]?.metadata?.speakerAnalysis;
  if (isLayoutsWithVisionDataEnabled(clip)) {
    return speakersAnalysis && Object.keys(speakersAnalysis).length > 0;
  }

  return true;
};

export const hasMoreThanOneCompositeSegment = (clip: Clip): boolean => {
  const allComposites = getAllCompositeSegments(clip);
  return allComposites.length > 1;
};

export const getIsClipUsingDefaultLanguage = (clip: Clip): boolean => {
  return (
    !clip.asset_metadata.original_lang ||
    !clip.asset_metadata.selected_lang ||
    clip.asset_metadata.original_lang === clip.asset_metadata.selected_lang
  );
};
